import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import {
  DATA_EXPLORERS,
  DATA_LIBRARY,
} from "../../../../../../shared/lib/routesResolver";
import useAxios from "../../../../../../hooks/useAxios";
import useLoading from "../../../../../../shared/components/loading/useLoading";
import {
  API_DATASET_DIMENSIONS,
  API_DATASET_LIBRARY_BY_ID,
  API_DATASET_METRICS,
  API_DATASET_QUERY,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import { useTracking } from "react-tracking";
import BreadcrumbPanel from "../../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import {
  Card,
  CardContent,
} from "../../../../../../shared/components/card/Card";
import { Play } from "lucide-react";
import { Separator } from "../../../../../../shared/components/separator/Separator";
import { Button } from "../../../../../../shared/components/button/Button";
import DimensionMetricSelector from "./DimensionMetricSelector";
import FilterBuilder from "./FilterBuilder";
import {
  catchFunction,
  groupDataLibraryFilters,
} from "../../../../../../shared/lib/utils";
import ResultsTable from "./ResultsTable";
import DataLibraryContext from "../../../../../../contexts/dataLibraryContext";

const DataLibraryLens = () => {
  const { id } = useParams();
  const axios = useAxios();
  const { loading, setLoading } = useLoading();
  const context = useContext(DataLibraryContext);
  const [dataset, setDataset] = useState(context.dataset);
  const { trackEvent } = useTracking();
  // ************************************
  const [dimensions, setDimensions] = useState(context.dimensions);
  const [metrics, setMetrics] = useState(context.metrics);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [filters, setFilters] = useState([]);
  const [results, setResults] = useState([]);

  const breadcrumbItems = [
    {
      title: "dataLibrary.overview.breadcrumb.first",
      link: DATA_EXPLORERS,
    },
    { title: "dataLibrary.overview.breadcrumb.second", link: DATA_LIBRARY },
    {
      title: "dataLibrary.overview.breadcrumb.lens",
      titleOptions: { dataset: dataset?.name },
    },
  ];

  const findDataset = () => {
    setLoading(true);
    axios
      .get(API_DATASET_LIBRARY_BY_ID(id))
      .then((response) => {
        setDataset(response.data);
        context.setDataset(response.data);
      })
      .finally(() => setLoading(false));
  };

  const findDatasetDimensions = () => {
    axios.get(API_DATASET_DIMENSIONS(id)).then((response) => {
      const dimensions = response.data.map((dimension) => {
        return { ...dimension, possibleValues: [] };
      });
      setDimensions(dimensions);
      context.setDimensions(dimensions);
    });
  };

  const findDatasetMetrics = () => {
    axios.get(API_DATASET_METRICS(id)).then((response) => {
      setMetrics(response.data);
      context.setMetrics(response.data);
    });
  };

  // **********************************************************
  const handleSubmit = () => {
    const request = {
      dimensions: selectedDimensions,
      metrics: selectedMetrics,
      filters: groupDataLibraryFilters(filters).map((item) => {
        return {
          dimension: item.dimension.code,
          operation: item.operation.code,
          values: item.value,
        };
      }),
    };

    trackEvent({
      page: "datasets-library-lens",
      event: "button-clicked",
      data: {
        name: "run-analysis-button",
        dataset: id,
        request: request,
      },
    });

    setLoading(true);
    axios
      .post(API_DATASET_QUERY(id), request)
      .then((response) => {
        setResults(response.data);
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const canSubmit = selectedDimensions.length > 0 || selectedMetrics.length > 0;

  /*
  const groupFilters = (filters) => {
    const result = [];
    const groupMap = new Map();

    filters.forEach((item) => {
      const key = `${item.dimension.code}_${item.operation.id}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          dimension: item.dimension,
          operation: item.operation,
          values: [],
        });
      }

      groupMap.get(key).values.push(item.value);
      if (item.endValue) {
        groupMap.get(key).values.push(item.endValue);
      }
    });

    groupMap.forEach((group) => {
      result.push({
        dimension: group.dimension,
        operation: group.operation,
        value: group.values,
      });
    });

    return result;
  };
   */

  useEffect(() => {
    if (dataset === undefined || id !== dataset.code) {
      findDataset();
      findDatasetDimensions();
      findDatasetMetrics();
    }
    trackEvent({
      page: "datasets-library-lens",
      event: "page-load",
      data: { id: id },
    });
  }, [id]);

  return (
    <>
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="space-y-6">
          <DimensionMetricSelector
            dimensions={dimensions}
            metrics={metrics}
            selectedDimensions={selectedDimensions}
            selectedMetrics={selectedMetrics}
            onDimensionsChange={setSelectedDimensions}
            onMetricsChange={setSelectedMetrics}
          />
          <Separator />
          <FilterBuilder
            datasetCode={id}
            dimensions={dimensions}
            filters={filters}
            onFiltersChange={setFilters}
          />
          <Separator />
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Ready to analyze</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedDimensions.length} dimension(s),{" "}
                    {selectedMetrics.length} metric(s), {filters.length}{" "}
                    filter(s) selected
                  </p>
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || loading}
                  size="lg"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {loading ? "Analyzing..." : "Run Analysis"}
                </Button>
              </div>
            </CardContent>
          </Card>
          <Separator />
          <ResultsTable
            data={results}
            isLoading={loading}
            datasetCode={id}
            filters={filters}
            selectedDimensions={selectedDimensions}
            selectedMetrics={selectedMetrics}
          />
        </div>
      </div>
    </>
  );
};

export default DataLibraryLens;
